import { useContext } from 'react';
import AppState from 'AppState';
import EditContext from '../components/shared/edit/EditContext';
import { AppSiteDto } from '../types';
import RecordScope from 'types/__models__/RecordScope';
export interface SiteHostWithIdDto {
	hostId: string;
	hostUrl: string;
}

/**
 * Gets the first available site host for an edit page component given the items scope, if any.
 * Otherwise, falls back to the default site/host.
 */
export const useScopedSiteHost = () => {
	const { currentScope } = useContext(EditContext);

	if (currentScope) {
		return GetSiteHost(currentScope.value);
	}

	return GetDefaultSiteHost();
};

/**
 * Gets the static asset host from the app state. This is the host that should be used for serving assets stored in the codebase.
 */
export const GetStaticAssetHost = () => {
	return AppState.staticAssetHost;
};

/**
 * Gets the first available site host for the given scopeId. Otherwise , falls back to the default site/host.
 * @param scopeId The scope of the item we're returning the host for, if any.
 */
export const GetSiteHost = (scopeId?: string) => {
	if (scopeId) {
		let site = AppState.sites.find((s) => s.id === scopeId);
		const siteCollection = AppState.siteCollections.find((c) => c.id === scopeId);

		if (siteCollection) {
			const siteId = siteCollection.sites.length > 0 ? siteCollection.sites[0].siteId : undefined;
			if (siteId) {
				site = AppState.sites.find((s) => s.id === siteId);
			}
		}

		if (site) {
			return GetHostForSite(site);
		}
	}

	return GetDefaultSiteHost();
};

/**
 * Returns the list of sites with host for the View Live components.
 */
export const GetSiteHosts = (scopeId?: string): ViewSiteHost[] => {
	if (scopeId) {
		const site = AppState.sites.find((s) => s.id === scopeId);
		if (site) {
			return [{ name: site.name, hosts: GetHostsForSite(site) }];
		}

		const siteCollection = AppState.siteCollections.find((c) => c.id === scopeId);
		if (siteCollection) {
			return siteCollection.sites
				.map((s) => {
					const site = AppState.sites.find((site) => site.id === s.siteId);
					if (!site) {
						return {};
					}
					return { name: site.name, hosts: GetHostsForSite(site) };
				})
				.filter((s) => s.hosts);
		}
	}

	return AppState.sites.map((s) => {
		return { name: s.name, hosts: GetHostsForSite(s), id: s.id };
	});
};

export interface ViewSiteHostWithId {
	name?: string;
	hosts?: SiteHostWithIdDto[];
	id?: string;
}

export const GetSiteHostsWithIds = (scopeId?: string): ViewSiteHostWithId[] => {
	if (scopeId) {
		const site = AppState.sites.find((s) => s.id === scopeId);
		if (site) {
			return [{ name: site.name, hosts: GetHostsForSiteWithIds(site) }];
		}

		const siteCollection = AppState.siteCollections.find((c) => c.id === scopeId);
		if (siteCollection) {
			return siteCollection.sites
				.map((s) => {
					const site = AppState.sites.find((site) => site.id === s.siteId);
					if (!site) {
						return {};
					}
					return { name: site.name, hosts: GetHostsForSiteWithIds(site) };
				})
				.filter((s) => s.hosts);
		}
	}

	return AppState.sites.map((s) => {
		return { name: s.name, hosts: GetHostsForSiteWithIds(s), id: s.id };
	});
};

/**
 *
 *
 * @param {string} [scopeId]
 * @return {*}  {ViewSiteCulture[]}
 */

export const GetSiteHostCultures = (scopeId?: string): ViewSiteCulture[] => {
	if (scopeId) {
		const site = AppState.sites.find((s) => s.id === scopeId);
		if (site) {
			return [{ name: site.name, cultures: GetCulturesForSite(site) }];
		}
		const siteCollection = AppState.siteCollections.find((c) => c.id === scopeId);
		if (siteCollection) {
			return siteCollection.sites
				.map((s) => {
					const site = AppState.sites.find((site) => site.id === s.siteId);
					if (!site) {
						return {};
					}
					return { name: site.name, cultures: GetCulturesForSite(site) };
				})
				.filter((s) => s.cultures);
		}
	}

	return AppState.sites.map((s) => {
		return { name: s.name, cultures: GetCulturesForSite(s), id: s.id };
	});
};

/**
 * Checks whether the a collection of sites has more than one host
 * @param sites The collection of sites to check
 */
export const HasSiteWithAmbiguousHost = (sites: ViewSiteHost[]): boolean => {
	return sites.length > 1 || sites.some((s) => s.hosts && s.hosts.length > 1);
};

/**
 * Represents a site with host for the View Live components.
 */
export interface ViewSiteHost {
	id?: string;
	hosts?: string[];
	name?: string;
}

/**
 * Represents a site with cultures for the View Live components.
 */
export interface ViewSiteCulture {
	id?: string;
	cultures?: string[];
	name?: string;
}

/**
 * Gets the default site's default host.
 */
export const GetDefaultSiteHost = () => {
	const defaultSite = AppState.sites.find((site) => site.isDefault);
	if (defaultSite) {
		return GetHostForSite(defaultSite);
	}
	return '';
};

/**
 * Returns the first host for a site
 * @param site The site to fetch the host from
 */
const GetHostForSite = (site: AppSiteDto): string => {
	const h = site.hosts[0];
	return 'https://' + h.host + (h.port ? ':' + h.port : '') + (h.subdirectory ? '/' + h.subdirectory : '');
};

/**
 *
 *
 * @param {AppSiteDto} site
 * @return {*}  {string[]}
 */
const GetCulturesForSite = (site: AppSiteDto): string[] => {
	const siteHosts = site.hosts;
	const cultureIds = siteHosts.map((host) => host.cultureId || '');
	cultureIds.push(site.cultureId);
	return cultureIds.filter((v, i, s) => v !== '' && s.indexOf(v) === i);
};

/**
 * Returns all the hosts for a site
 * @param site The site to fetch hosts from
 */
const GetHostsForSite = (site: AppSiteDto): string[] => {
	const siteHosts = site.hosts;
	return siteHosts.map(
		(host) =>
			'https://' +
			host.host +
			(host.port ? ':' + host.port : '') +
			(host.subdirectory ? '/' + host.subdirectory : ''),
	);
};

const GetHostsForSiteWithIds = (site: AppSiteDto): SiteHostWithIdDto[] => {
	const siteHosts = site.hosts;
	return siteHosts.map((host) => {
		return {
			hostId: host.id,
			hostUrl:
				'https://' +
				host.host +
				(host.port ? ':' + host.port : '') +
				(host.subdirectory ? '/' + host.subdirectory : ''),
		};
	});
};

/**
 * Returns sites limited to associated scope
 */
export const GetScopedSites = (scope: RecordScope | undefined): AppSiteDto[] => {
	if (scope?.type === 'Site') {
		return AppState.sites.filter((s) => s.id === scope.value);
	} else if (scope?.type === 'Site Collection') {
		return AppState.sites.filter((s) => s.siteCollections.some((sc) => sc.siteCollectionId === scope.value));
	} else {
		return AppState.sites;
	}
};
